import { z } from 'zod'

import { ShareScopeSchema } from '~/@entities/Content'
import { AssignTargetSchema, GuestSchema } from '~/@entities/Guest'
import { ProblemLevelSchema } from '~/@entities/Problem'
import { AllGradeSubjectSchema, RevisionSchema, SchoolTypeSchema } from '~/@entities/SchoolSystem'
import { WorksheetTagSchema } from '~/@entities/Worksheet'

/***************** 목록 상태 *****************/
// 활성 목록과 휴지통. 서버 ContentStatus 중 이 목록이 쓰는 둘이다.
export const ENTRANCE_WORKSHEET_STATUS = ['ACTIVE', 'HIDDEN'] as const

export const EntranceWorksheetStatusSchema = z.enum(ENTRANCE_WORKSHEET_STATUS)
export type EntranceWorksheetStatus = z.infer<typeof EntranceWorksheetStatusSchema>

/***************** 배정 상태 *****************/
export const GUEST_ENTRANCE_STATUS = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED'] as const
export const GuestEntranceStatusSchema = z.enum(GUEST_ENTRANCE_STATUS)
export type GuestEntranceStatus = z.infer<typeof GuestEntranceStatusSchema>

// 시험지에 배정된 비재원생 한 명. guestEntranceWorksheetId 가 배정 한 건의 id 다.
export const EntranceWorksheetAssigneeSchema = z.object({
  guestEntranceWorksheetId: z.number(),
  guestId: GuestSchema.shape.id,
  guestName: z.string(),
  status: GuestEntranceStatusSchema,
  /** 배정된 입학시험 회차. (guestId, admissionRefId) 로 배정 모달의 행과 맞춘다 */
  admissionRefId: AssignTargetSchema.shape.admissionRefId,
})
export type EntranceWorksheetAssignee = z.infer<typeof EntranceWorksheetAssigneeSchema>

// 크레버스 입학고사의 학습지형 시험지 한 건(센터·센터간 공유·휴지통 목록).
export const EntranceWorksheetSchema = z.object({
  id: z.number(),
  revision: RevisionSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  title: z.string(),
  titleTag: z.string(),
  tag: WorksheetTagSchema,
  chapter: z.string(),
  autoScorable: z.boolean(),
  writer: z.string(),
  createDatetime: z.iso.datetime({ local: true }),
  problemCount: z.number(),
  level: ProblemLevelSchema,
  shareScope: ShareScopeSchema,
  assignList: z.array(EntranceWorksheetAssigneeSchema),
})
export type EntranceWorksheet = z.infer<typeof EntranceWorksheetSchema>

/** 생성·복제 결과. 생성은 늘 센터(ACADEMY) 범위다 */
export const EntranceWorksheetCreationSchema = z.object({
  creverseEntranceWorksheetId: EntranceWorksheetSchema.shape.id,
  shareScope: ShareScopeSchema,
})
export type EntranceWorksheetCreation = z.infer<typeof EntranceWorksheetCreationSchema>

// 배정 결과. 이번 요청으로 새로 배정된 건만 싣는다(이미 배정된 조합은 서버가 건너뛴다). 배정 id 를 목록 응답과
// 다른 이름(creverseGuestEntranceWorksheetId)으로 낸다.
export const EntranceWorksheetAssignEchoSchema = z.object({
  assignDatetime: z.iso.datetime({ local: true }),
  assignList: z.array(
    z.object({
      creverseGuestEntranceWorksheetId:
        EntranceWorksheetAssigneeSchema.shape.guestEntranceWorksheetId,
      worksheetId: EntranceWorksheetSchema.shape.id,
      guestId: GuestSchema.shape.id,
      guestName: z.string(),
      admissionRefId: AssignTargetSchema.shape.admissionRefId,
      status: GuestEntranceStatusSchema,
    }),
  ),
})
export type EntranceWorksheetAssignEcho = z.infer<typeof EntranceWorksheetAssignEchoSchema>

/***************** 비재원생별 배정 *****************/

// 비재원생 한 명에게 배정된 시험지 한 건. 시험지 요약의 학교급 필드 이름이 school 이다.
export const GuestEntranceWorksheetSchema = z.object({
  id: EntranceWorksheetAssigneeSchema.shape.guestEntranceWorksheetId,
  /** 보고서 공개 여부. 공유 전 null, 공개 true, 회수 false */
  reportIsOpen: z.boolean().nullable(),
  assignDatetime: z.iso.datetime({ local: true }),
  /** 응시일(입학시험 시험일 00:00) */
  openDatetime: z.iso.datetime({ local: true }),
  scoreDatetime: z.iso.datetime({ local: true }).nullable(),
  correctCount: z.number(),
  wrongCount: z.number(),
  /** 0~100. 미응시면 0 */
  score: z.number(),
  status: GuestEntranceStatusSchema,
  admissionRefId: AssignTargetSchema.shape.admissionRefId,
  /** 배정이 가리키는 시험 회차명. 회차가 연결되지 않으면 null */
  admissionName: z.string().nullable(),
  worksheet: z.object({
    id: EntranceWorksheetSchema.shape.id,
    revision: RevisionSchema,
    school: SchoolTypeSchema,
    grade: AllGradeSubjectSchema,
    title: z.string(),
    titleTag: z.string(),
    chapter: z.string(),
    tag: WorksheetTagSchema,
    writer: z.string(),
    problemCount: z.number(),
    level: ProblemLevelSchema,
    autoScorable: z.boolean(),
  }),
})
export type GuestEntranceWorksheet = z.infer<typeof GuestEntranceWorksheetSchema>

// 채점 결과. 목록 행을 갱신할 필드만 싣는다. score 는 서버 선언이 nullable 이다(늘 계산해 채우지만 타입은 열려 있다).
export const GuestEntranceScoringResultSchema = z.object({
  id: GuestEntranceWorksheetSchema.shape.id,
  scoreDatetime: GuestEntranceWorksheetSchema.shape.scoreDatetime,
  score: z.number().nullable(),
  status: GuestEntranceStatusSchema,
  correctCount: z.number(),
  wrongCount: z.number(),
})
export type GuestEntranceScoringResult = z.infer<typeof GuestEntranceScoringResultSchema>
